import React, { useEffect, useState, useMemo } from 'react';
import { chatServerInstanceWithToken } from '@/helpers/chat_server_instance';
import moment from 'moment';
import { PROFILE_IMAGE_SERVER_URL, SERVER_IMAGES_URL } from '@/constants';

export const Conversations = ({ onSelectChat, socket }) => {

    const [chats, setChats] = useState([]);
    const userId = JSON.parse(localStorage.getItem('data'))?.id;
    const [searchTerm, setSearchTerm] = useState('');
    const [sortDirection, setSortDirection] = useState('asc'); // Default to ascending

    useEffect(() => {
        chatServerInstanceWithToken.get(`/getUserChats/${userId}`)
            .then(response => {
                setChats(response.data);
            })
            .catch(error => console.error("Error fetching chats:", error));
    }, [userId]);

    // Socket listener for new messages
    useEffect(() => {
        socket.on('newMessage', (data) => {
            // console.log('New message received:', data);
            setChats(prevChats => {
                const updatedChats = [...prevChats];
                const chatIndex = updatedChats.findIndex(chat => chat.chat_id === data.chat_id);

                if (chatIndex !== -1) {
                    const chat = updatedChats[chatIndex];

                    updatedChats[chatIndex] = {
                        ...chat,
                        hasUnreadMessages: data.sender_id !== userId ? true : chat.hasUnreadMessages,
                        unreadMessageCount: data.sender_id !== userId ? (chat.unreadMessageCount || 0) + 1 : chat.unreadMessageCount,
                        last_message: {
                            id: data?.newMessage?.message_id,
                            message: data?.newMessage?.message,
                            sender_id: data?.newMessage?.sender_id,
                            sender_name: data?.newMessage?.sender_name,
                            status: data?.newMessage?.status,
                            created_at: data?.newMessage?.created_at
                        }
                    };

                    // Move updated chat to the top
                    const updatedChat = updatedChats.splice(chatIndex, 1)[0];
                    updatedChats.unshift(updatedChat);
                }
                return updatedChats;
            });
        });

        // Listen for message updates (unread status)
        socket.on('messageUpdated', (data) => {
            setChats(prevChats =>
                prevChats.map(chat =>
                    chat.chat_id === data.chat_id && userId === data.sender_id
                        ? { ...chat, hasUnreadMessages: true, unreadMessageCount: (chat.unreadMessageCount || 0) + 1 }
                        : chat
                )
            );
        });

        return () => {
            socket.off('newMessage');
            socket.off('messageUpdated');
        };

    }, [socket, userId]);

    useEffect(() => {
        chats?.length > 0 && chats.forEach(chat => {
            socket.emit('joinChat', chat?.chat_id);
        });

    }, [chats, socket]); // Empty dependency array ensures this effect runs only once on initial render

    const handleSelectChat = async (chat) => {
        console.log(chat)
        onSelectChat(chat);
        if (chat.hasUnreadMessages) {
            try {
                await chatServerInstanceWithToken.put(`/markMessagesAsRead/${chat.chat_id}`, { user_id: userId });
                setChats(chats.map(c => c.chat_id === chat.chat_id ? { ...c, hasUnreadMessages: false } : c));
            } catch (error) {
                console.error("Error marking messages as read:", error);
            }
        }
    };

    // Memoize the filtered and sorted chats based on searchTerm and chats
    const filteredAndSortedChats = useMemo(() => {
        const filteredChats = chats.filter(chat => {
            const user = chat.participant_2.id === userId ? chat.participant_1 : chat.participant_2;
            return user.name.toLowerCase().includes(searchTerm.toLowerCase());
        });

        // Sort chats by display_name
        return filteredChats.sort((a, b) => {
            const userA = a.participant_2.id === userId ? a.participant_1 : a.participant_2;
            const userB = b.participant_2.id === userId ? b.participant_1 : b.participant_2;
            const nameA = userA.name.toLowerCase();
            const nameB = userB.name.toLowerCase();

            if (sortDirection === 'asc') {
                return nameA.localeCompare(nameB); // Ascending order
            } else {
                return nameB.localeCompare(nameA); // Descending order
            }
        });
    }, [chats, searchTerm, userId, sortDirection]); // Recompute when chats, searchTerm, or sortDirection change

    const toggleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    return (
        <div>
            <div className="flex items-center px-2 rounded-md border-soft">
                <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} // Update search term
                    className="flex-1 p-2 outline-none"
                />
                <i className="text-gray-500 fa fa-search"></i>

                {/* Sort Toggle Button */}
                <i
                    className={`fa fa-sort-${sortDirection === 'asc' ? 'alpha-down' : 'alpha-up'} text-gray-500 cursor-pointer ml-2`}
                    onClick={toggleSortDirection}
                ></i>
            </div>
            {filteredAndSortedChats.length === 0 ? (
                <p className="text-gray-500">No chats available.</p>
            ) : (
                <ul className='!m-0 !p-0'>
                    {filteredAndSortedChats.map(chat => {
                        const user = chat.participant_2.id === userId ? chat.participant_1 : chat.participant_2;
                        return (
                            <li
                                key={chat.chat_id}
                                className={`flex items-center p-3 border-b border-soft hover:bg-gray-200 cursor-pointer list-none ${chat.hasUnreadMessages ? 'font-bold' : ''}`}
                                onClick={() => handleSelectChat(chat)}
                            >
                                {/* User Image */}
                                <img
                                    src={`${PROFILE_IMAGE_SERVER_URL}/${user?.image}` || 'images/avatar/avatar-bg.png'}
                                    alt={'User'}
                                    className="object-cover w-8 h-8 mr-3 rounded-full"
                                />


                                {/* Chat Details */}
                                <div className='flex justify-between w-full'>
                                    <p className="font-semibold !m-0 flex flex-col">
                                        <span className='font-bold text-md'>{user.name} </span>
                                        <span className='text-xs'>(<span className='text-red-500'>Reservation Details :</span> Booking # {chat?.booking_id})</span>
                                        <span className="text-sm font-medium truncate max-w-[200px] inline-block">
                                            {chat?.last_message?.sender_name} : {chat?.last_message?.message}
                                        </span>
                                        <span className='text-xs'>   {moment(chat?.last_message?.created_at).format('DD MMM YYYY h:mm A')}</span>

                                    </p>
                                    {/* Unread Messages Badge */}
                                    {chat.hasUnreadMessages && (
                                        <span className=" bg-green-500 text-white text-xs rounded-full px-3 py-1 opacity-75 !h-fit">
                                            {chat.unreadMessageCount}
                                        </span>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};